import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { ServiceItem } from '../types';

interface Props {
  heading: string;
  subheading: string;
  services: ServiceItem[];
}

function ServiceCard({ icon, title, desc, delay }: ServiceItem & { delay: number }) {
  const { ref, isVisible } = useScrollAnimation(0.05);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`group bg-white rounded-2xl p-5 border border-[#E0C78A]/20 hover:border-[#C8A96A]/60 hover:shadow-none transition-all duration-500 cursor-default ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#C8A96A]/15 to-[#A8894F]/10 rounded-xl mb-4 group-hover:from-[#C8A96A]/30 transition-all duration-300">
        <i className={`${icon} text-[#C8A96A] text-xl`} />
      </div>
      <h3 className="font-bold text-[#0A0A0A] text-sm mb-2 font-playfair leading-snug">{title}</h3>
      <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
    </div>
  );
}

export default function ServiceGrid({ heading, subheading, services }: Props) {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();

  return (
    <section id="services-detail" className="py-20 bg-[#F9F5EE] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, #C8A96A 1px, transparent 1px)', backgroundSize: '40px 40px' }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#C8A96A]/30 rounded-full mb-4">
            <i className="ri-tools-fill text-[#C8A96A] text-xs" />
            <span className="text-[#A8894F] text-xs tracking-widest uppercase font-medium">What&apos;s Included</span>
          </div>
          <h2 className="font-playfair text-3xl md:text-5xl font-bold text-[#0A0A0A] leading-tight">
            {heading.split(' ').slice(0, -1).join(' ')}{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#A8894F] bg-clip-text text-transparent">
              {heading.split(' ').slice(-1)[0]}
            </span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-sm md:text-base">{subheading}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {services.map((service, i) => (
            <ServiceCard key={service.title} {...service} delay={i * 40} />
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm mb-4">Not seeing exactly what you need? We cover a wide range of electrical work.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:01529265060"
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold px-6 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-phone-fill" />
              Call 01529 265060
            </a>
            <button
              onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center justify-center gap-2 border-2 border-[#C8A96A] text-[#A8894F] hover:bg-[#C8A96A]/10 font-bold px-6 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-file-list-3-line" />
              Free Quote
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
